import { Api, ApiImpl } from "@/service/api/Api";
import { UserModule } from "@/store/modules/UserModule"
import { getModule } from "vuex-module-decorators";
import { ErrorMessageServiceImpl } from "@/service/ErrorMessageService";
import VueI18n from "vue-i18n";
import Vue from "vue";
import App from "@/App.vue";
import axios from "axios";
import { Router } from "@/router/Router";
import { VueRouter } from "vue-router/types/router";
import { LocaleRoot } from "@/localization/LocaleRoot";
import { AppModule } from "@/store/modules/AppModule";
import { Store } from 'vuex'
import store, { RootState } from "@/store/store";
import { ContactUsService } from "@/service/ContactUsService";
import { Analytics } from "@/utils/analytics/Analytics";
import { YandexMetrika } from "@/utils/analytics/YandexMetrika";
import { MyEventsModule } from "@/store/modules/MyEventsModule";
import { MyEventsService } from "@/service/MyEventsService";
import { registerFilters } from "@/utils/filters";
import { AuthService } from "@/service/AuthService";
import { OrganizationModule } from "@/store/modules/OrganizationModule";
import { OrganizationService } from "@/service/OrganizationService";
import hotkeys from "hotkeys-js";
import { ErrorMessageService } from "frontend-common";

export class CompositionRoot {

    private _vue: Vue | undefined
    private _router: Router | undefined
    private _localeRoot: LocaleRoot | undefined
    private _userModule: UserModule | undefined
    private _appModule: AppModule | undefined
    private _myEventsModule: MyEventsModule | undefined
    private _organizationModule: OrganizationModule | undefined
    private _analytics: Analytics | undefined
    private _api: Api | undefined
    private _contactUsService: ContactUsService | undefined
    private _errorMessageService: ErrorMessageService | undefined
    private _myEventsService: MyEventsService | undefined
    private _organizationService: OrganizationService | undefined
    private _authService: AuthService | undefined

    get vue(): Vue {
        if (!this._vue) {
            registerFilters()
            this._vue = new Vue({
                el: "#app",
                router: this.vueRouter,
                store: this.store,
                i18n: this.i18n,
                cr: this,
                render: h => h(App)
            })
            hotkeys.filter = function (event) {
                return true
            }
        }
        return this._vue;
    }

    get localeRoot(): LocaleRoot {
        if (!this._localeRoot) {
            this._localeRoot = new LocaleRoot(this.appModule)
        }
        return this._localeRoot;
    }

    get store(): Store<RootState> {
        return store
    }

    get userModule(): UserModule {
        if (!this._userModule) {
            this._userModule = getModule(UserModule)
        }
        return this._userModule;
    }

    get appModule(): AppModule {
        if (!this._appModule) {
            this._appModule = getModule(AppModule)
            this._appModule.setLocale(this.localeRoot.locale.getLocale())
        }
        return this._appModule;
    }

    get myEventsModule(): MyEventsModule {
        if (!this._myEventsModule) {
            this._myEventsModule = getModule(MyEventsModule)
        }
        return this._myEventsModule;
    }

    get organizationModule(): OrganizationModule {
        if (!this._organizationModule) {
            this._organizationModule = getModule(OrganizationModule)
        }
        return this._organizationModule;
    }

    get analytics(): Analytics {
        if (!this._analytics) {
            this._analytics = new YandexMetrika(62541280)
        }
        return this._analytics;
    }

    get i18n(): VueI18n {
        return this.localeRoot.i18n
    }

    get vueRouter(): VueRouter {
        return this.router.router
    }

    get router(): Router {
        if (!this._router) {
            this._router = new Router(this.i18n, this.userModule, this.appModule, this.analytics)
        }
        return this._router
    }

    get api(): Api {
        if (!this._api) {
            this._api = new ApiImpl()
        }
        return this._api;
    }

    get contactUsService(): ContactUsService {
        if (!this._contactUsService) {
            this._contactUsService = new ContactUsService(this.api)
        }
        return this._contactUsService
    }

    get errorMessageService(): ErrorMessageService {
        if (!this._errorMessageService) {
            this._errorMessageService = new ErrorMessageServiceImpl(this.i18n)
        }
        return this._errorMessageService;
    }

    get myEventsService(): MyEventsService {
        if (!this._myEventsService) {
            this._myEventsService = new MyEventsService(this.api, this.myEventsModule)
        }
        return this._myEventsService
    }

    get organizationService(): OrganizationService {
        if (!this._organizationService) {
            this._organizationService = new OrganizationService(this.api, this.organizationModule)
        }
        return this._organizationService
    }

    get authService(): AuthService {
        if (!this._authService) {
            this._authService = new AuthService(this.api, this.userModule, this.myEventsService,
                this.organizationService, this.localeRoot.locale)
        }
        return this._authService
    }

    setupAxios() {
        const host = process.env.VUE_APP_API_HOST
        axios.defaults.baseURL = host + "/api"
        axios.defaults.headers.common["Content-Type"] = "application/json"
        axios.defaults.headers.common["Accept"] = "application/json"
        const userModule = this.userModule;
        axios.interceptors.request.use(function (config) {
            if (userModule.token) {
                config.headers["Authorization"] = "bearer " + userModule.token
            }
            return config
        })
        axios.interceptors.response.use(resp => {
            return resp
        }, error => {
            if (error.response && error.response.status === 401) {
                this.userModule.clearAll()
            }
            return Promise.reject(error)
        })
    }
}

const cr = new CompositionRoot()

export default cr
